/**
 * LanguagePicker — tiny inline selector for the active locale.
 *
 * Renders the current language plus a button row to switch to any of the
 * other supported languages. Defaults to inline horizontal layout but
 * can be styled via the `compact` prop for menu use.
 *
 * Persistence is automatic — i18next's LanguageDetector caches the
 * selection in localStorage so refreshing or revisiting keeps the choice.
 */
import { useEffect, useState } from "react";
import { SUPPORTED_LOCALES, setLocale, currentLocale, type LocaleCode } from "@/lib/i18n";

interface LanguagePickerProps {
  /** When true, use a small one-row inline pill set. */
  compact?: boolean;
}

export function LanguagePicker({ compact = false }: LanguagePickerProps) {
  const [active, setActive] = useState<LocaleCode>(currentLocale());

  // Keep state in sync if i18n's language gets changed elsewhere.
  useEffect(() => {
    const id = window.setInterval(() => {
      const c = currentLocale();
      if (c !== active) setActive(c);
    }, 1000);
    return () => window.clearInterval(id);
  }, [active]);

  const onPick = async (code: LocaleCode) => {
    await setLocale(code);
    setActive(code);
  };

  return (
    <div className={compact ? "flex flex-wrap gap-1" : "flex flex-wrap gap-2"}>
      {SUPPORTED_LOCALES.map((l) => {
        const isActive = l.code === active;
        return (
          <button
            key={l.code}
            type="button"
            onClick={() => void onPick(l.code)}
            className={`rounded-full px-2.5 py-1 text-xs font-semibold transition ${
              isActive
                ? "bg-amber-300 text-emerald-950"
                : "bg-white/5 text-white/70 hover:bg-white/10 hover:text-white"
            } ${compact ? "py-0.5 text-[11px]" : ""}`}
            title={l.name}
          >
            {l.native}
          </button>
        );
      })}
    </div>
  );
}
